import React, {Dispatch, SetStateAction, useEffect, useState} from 'react';
import {StyleProp, StyleSheet, TextStyle, View, ViewStyle} from 'react-native';
import DropDownPicker, {
  DropDownPickerProps as RNDropDownPickerProps,
  ItemType,
} from 'react-native-dropdown-picker';

import {universal} from '../../design-system/tokens';
import ChevronDownImg from '../../images/svg/icons/FieldChevronDown.svg';
import {isValidDateOfBirth, parseDateOfBirth, splitDateOfBirth} from '../../utils/date';

import {fontFamilyStyle} from './Text';

const months = [
  {label: 'January', value: '01'},
  {label: 'February', value: '02'},
  {label: 'March', value: '03'},
  {label: 'April', value: '04'},
  {label: 'May', value: '05'},
  {label: 'June', value: '06'},
  {label: 'July', value: '07'},
  {label: 'August', value: '08'},
  {label: 'September', value: '09'},
  {label: 'October', value: '10'},
  {label: 'November', value: '11'},
  {label: 'December', value: '12'},
];

const days = Array.from({length: 31}, (_, i) => ({
  label: `${i + 1}`,
  value: `${i + 1}`.padStart(2, '0'),
}));
const currentYear = new Date().getFullYear() - 18; // 18: years old minimum
// 82: 100 - 18, 100: years old maximum
const years = Array.from({length: 82}, (_, i) => ({
  label: `${currentYear - i}`,
  value: `${currentYear - i}`,
}));

// SHORTCUT: colour and geometry literals - the four rgb() colours, opacity 0.5,
// minHeight 40 and the 28% / 38% column widths in `styles` below, and the
// chevron's CHEVRON_SIZE (no icon-size token exists). They predate
// the design system and do not use the Field States palette in
// design-system/form/FormField.tsx. Ceiling: this picker, on every screen
// that renders it. Upgrade path: colour and spacing modules under
// app/design-system/tokens/ - DIS-1737 blocks them, DIS-1735 is the epic.
const styles = StyleSheet.create({
  main: {
    minHeight: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  dayYearDropdownContainer: {
    width: '28%',
  },
  monthDropdownContainer: {
    width: '38%',
  },
  dropDown: {
    borderColor: 'rgb(134, 147, 158)',
    ...fontFamilyStyle,
  },
  dropDownDisabled: {
    ...fontFamilyStyle,
    backgroundColor: 'rgb(243, 243, 243)',
    color: 'rgb(122, 122, 122)',
    borderColor: 'rgb(156, 163, 175)',
    opacity: 0.5,
  },
});

// Figma "Loyalty Sign-Up Redesign" chevron. Its colour matches the library light
// theme's label colour (Colors.BLACK, react-native-dropdown-picker 5.4.6), which
// is the text colour every caller renders, because none passes a textStyle colour.
const CHEVRON_SIZE = 16;
const CHEVRON_COLOR = universal.black;

const chevronStyles = StyleSheet.create({
  box: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  up: {
    transform: [{rotate: '180deg'}],
  },
});

type ArrowIconProps = {style: StyleProp<ViewStyle>};

// The library hands in its 20x20 arrow box style; the chevron is centred in it.
// The rotation sits on the View, not the SVG, because on web the SVG is a DOM
// element that cannot take a React Native transform array.
const ArrowDownIcon = ({style}: ArrowIconProps) => (
  <View style={[style, chevronStyles.box]}>
    <ChevronDownImg width={CHEVRON_SIZE} height={CHEVRON_SIZE} color={CHEVRON_COLOR} />
  </View>
);

const ArrowUpIcon = ({style}: ArrowIconProps) => (
  <View style={[style, chevronStyles.box, chevronStyles.up]}>
    <ChevronDownImg width={CHEVRON_SIZE} height={CHEVRON_SIZE} color={CHEVRON_COLOR} />
  </View>
);

type DropDownPickerProps = RNDropDownPickerProps<string>;
type PartialDropDownPickerProps = Partial<DropDownPickerProps>;

const commonDropDownPickerProps: PartialDropDownPickerProps = {
  listMode: 'SCROLLVIEW',
  multiple: false,
  ArrowDownIconComponent: ArrowDownIcon,
  ArrowUpIconComponent: ArrowUpIcon,
  textStyle: {
    ...fontFamilyStyle,
  },
};

const getInitialDateOfBirthValues = (dateString?: string) => {
  if (!dateString) {
    return {
      day: null,
      month: null,
      year: null,
    };
  }

  return splitDateOfBirth(dateString);
};

type Props = {
  style?: StyleProp<ViewStyle>;
  dropDownContainerStyle?: StyleProp<ViewStyle>;
  dropDownStyle?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
  onChange: (dateString: string) => void;
  onInvalidDate: (errorMessage: string) => void;
  initialDate?: string;
  disabled?: boolean;
};
const DateOfBirthInput = ({
  onChange,
  onInvalidDate,
  style,
  dropDownContainerStyle,
  dropDownStyle,
  textStyle,
  initialDate,
  disabled,
}: Props) => {
  const [isDayDropdownOpen, setDayDropdownOpen] = useState(false);
  const [isMonthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const [isYearDropdownOpen, setYearDropdownOpen] = useState(false);

  const initialValues = getInitialDateOfBirthValues(initialDate);

  const [day, setDay] = useState<string | null>(initialValues.day);
  const [month, setMonth] = useState<string | null>(initialValues.month);
  const [year, setYear] = useState<string | null>(initialValues.year);

  // Sync internal state when initialDate prop changes (e.g., form reset)
  useEffect(() => {
    const values = getInitialDateOfBirthValues(initialDate);
    setDay(values.day);
    setMonth(values.month);
    setYear(values.year);
  }, [initialDate]);

  // https://hossein-zare.github.io/react-native-dropdown-picker-website/docs/usage#single-item 🤷🏻‍♂️
  const [dayItems, setDayItems] = useState(days);
  const [monthItems, setMonthItems] = useState(months);
  const [yearItems, setYearItems] = useState(years);

  const handleDayOpen: Dispatch<SetStateAction<boolean>> = (value: SetStateAction<boolean>) => {
    setDayDropdownOpen(value);
    setMonthDropdownOpen(false);
    setYearDropdownOpen(false);
  };

  const handleMonthOpen: Dispatch<SetStateAction<boolean>> = (value: SetStateAction<boolean>) => {
    setMonthDropdownOpen(value);
    setDayDropdownOpen(false);
    setYearDropdownOpen(false);
  };

  const handleYearOpen: Dispatch<SetStateAction<boolean>> = (value: SetStateAction<boolean>) => {
    setYearDropdownOpen(value);
    setDayDropdownOpen(false);
    setMonthDropdownOpen(false);
  };

  const handleSelectDay = (item: ItemType<string>) => handleSelectDate(year, month, item.value);
  const handleSelectMonth = (item: ItemType<string>) => handleSelectDate(year, item.value, day);
  const handleSelectYear = (item: ItemType<string>) => handleSelectDate(item.value, month, day);

  const handleSelectDate = (
    selectedYear: string | null | undefined,
    selectedMonth: string | null | undefined,
    selectedDay: string | null | undefined,
  ) => {
    if (!selectedYear || !selectedMonth || !selectedDay) return null;

    const dobString = parseDateOfBirth(selectedYear, selectedMonth, selectedDay);

    if (isValidDateOfBirth(dobString)) {
      onChange(dobString);
    } else {
      onInvalidDate('Invalid date selected');
    }
  };

  return (
    <View style={[styles.main, style]}>
      <DropDownPicker
        {...commonDropDownPickerProps}
        containerStyle={styles.dayYearDropdownContainer}
        style={[styles.dropDown, dropDownStyle]}
        textStyle={
          disabled ? styles.dropDownDisabled : [commonDropDownPickerProps.textStyle, textStyle]
        }
        dropDownContainerStyle={dropDownContainerStyle}
        listParentContainerStyle={{zIndex: 1}}
        items={dayItems}
        onSelectItem={handleSelectDay}
        open={isDayDropdownOpen}
        placeholder="Day"
        setItems={setDayItems}
        setOpen={handleDayOpen}
        setValue={setDay}
        value={day}
        disabled={disabled}
        disabledStyle={styles.dropDownDisabled}
      />

      <DropDownPicker
        {...commonDropDownPickerProps}
        containerStyle={styles.monthDropdownContainer}
        style={[styles.dropDown, dropDownStyle]}
        textStyle={
          disabled ? styles.dropDownDisabled : [commonDropDownPickerProps.textStyle, textStyle]
        }
        dropDownContainerStyle={dropDownContainerStyle}
        items={monthItems}
        onSelectItem={handleSelectMonth}
        open={isMonthDropdownOpen}
        placeholder="Month"
        setItems={setMonthItems}
        setOpen={handleMonthOpen}
        setValue={setMonth}
        value={month}
        disabled={disabled}
        disabledStyle={styles.dropDownDisabled}
      />

      <DropDownPicker
        {...commonDropDownPickerProps}
        containerStyle={styles.dayYearDropdownContainer}
        style={[styles.dropDown, dropDownStyle]}
        textStyle={
          disabled ? styles.dropDownDisabled : [commonDropDownPickerProps.textStyle, textStyle]
        }
        dropDownContainerStyle={dropDownContainerStyle}
        items={yearItems}
        onSelectItem={handleSelectYear}
        open={isYearDropdownOpen}
        placeholder="Year"
        setItems={setYearItems}
        setOpen={handleYearOpen}
        setValue={setYear}
        value={year}
        disabled={disabled}
        disabledStyle={styles.dropDownDisabled}
      />
    </View>
  );
};

export default DateOfBirthInput;
